import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  ESTADO_FERIADO_LOJA_LABEL,
  formatDataBR,
  lojaLabel,
  type EstadoFeriadoLoja,
  type Feriado,
  type FeriadoLoja,
  type Loja,
} from "@/api/escala.service";

const ESTADOS: EstadoFeriadoLoja[] = ["FECHA", "ABRE", "NAO_E_FERIADO"];

interface EscalaFeriadoLojasModalProps {
  // Feriado das lojas — null fecha o modal.
  feriado: Feriado | null;
  // As lojas que o usuário enxerga (listar_lojas): são as que ele pode marcar.
  lojas: Loja[];
  // Administrador de Escala: o único que troca o padrão, que vale também nas lojas que os outros
  // não enxergam.
  escopoTotal: boolean;
  onClose: () => void;
  onSubmit: (padrao: EstadoFeriadoLoja, lojas: FeriadoLoja[]) => Promise<void>;
}

interface LinhaEstadoProps {
  rotulo: string;
  detalhe?: string;
  valor: EstadoFeriadoLoja;
  onChange: (estado: EstadoFeriadoLoja) => void;
  desabilitado?: boolean;
}

function LinhaEstado({ rotulo, detalhe, valor, onChange, desabilitado }: LinhaEstadoProps) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, py: 0.5 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {rotulo}
        </Typography>
        {detalhe && (
          <Typography variant="caption" color="text.secondary">
            {detalhe}
          </Typography>
        )}
      </Box>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={valor}
        aria-label={rotulo}
        onChange={(_, novo: EstadoFeriadoLoja | null) => novo && onChange(novo)}
      >
        {ESTADOS.map((e) => (
          <ToggleButton key={e} value={e} disabled={desabilitado} sx={{ whiteSpace: "nowrap" }}>
            {ESTADO_FERIADO_LOJA_LABEL[e]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

// O que o feriado é em cada loja. A primeira linha é o padrão, que vale para as lojas sem marcação e
// para as cadastradas depois; cada loja mostra o que vale nela, e a que fica igual ao padrão o segue
// quando ele muda. Só as marcações que fogem do padrão vão para o servidor.
export function EscalaFeriadoLojasModal({ feriado, lojas, escopoTotal, onClose, onSubmit }: EscalaFeriadoLojasModalProps) {
  const [padrao, setPadrao] = useState<EstadoFeriadoLoja>("FECHA");
  const [marcacoes, setMarcacoes] = useState<Map<string, EstadoFeriadoLoja>>(new Map());
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!feriado) return;
    setPadrao(feriado.padrao ?? "FECHA");
    setMarcacoes(new Map((feriado.lojas ?? []).map((l) => [l.loja_id, l.estado])));
  }, [feriado]);

  const ordenadas = useMemo(
    () =>
      [...lojas].sort(
        (a, b) => Number(b.ativo) - Number(a.ativo) || lojaLabel(a).localeCompare(lojaLabel(b), "pt-BR"),
      ),
    [lojas],
  );

  const trocarPadrao = (novo: EstadoFeriadoLoja) => {
    setPadrao(novo);
    setMarcacoes((m) => new Map([...m].filter(([, estado]) => estado !== novo)));
  };

  const marcar = (lojaId: string, estado: EstadoFeriadoLoja) =>
    setMarcacoes((m) => {
      const proximo = new Map(m);
      if (estado === padrao) proximo.delete(lojaId);
      else proximo.set(lojaId, estado);
      return proximo;
    });

  const handleSalvar = async () => {
    if (salvando) return;
    setSalvando(true);
    try {
      await onSubmit(
        padrao,
        [...marcacoes].map(([loja_id, estado]) => ({ loja_id, estado })),
      );
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={!!feriado} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Lojas — {feriado?.nome} ({feriado ? formatDataBR(feriado.data) : ""})
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1, pt: "8px !important" }}>
        <Alert severity="info" sx={{ typography: "caption" }}>
          <div>"Fecha" vira folga automática na geração.</div>
          <div>"Abre" é feriado com a loja funcionando.</div>
          <div>"Não é feriado" é dia comum naquela loja.</div>
        </Alert>
        <LinhaEstado
          rotulo="Demais lojas e as cadastradas depois"
          detalhe={!escopoTotal ? "Só o Administrador de Escala muda as demais lojas." : undefined}
          valor={padrao}
          onChange={trocarPadrao}
          desabilitado={!escopoTotal}
        />
        <Divider />
        {ordenadas.length === 0 && <Alert severity="info">Nenhuma loja disponível para marcar.</Alert>}
        {ordenadas.map((loja) => (
          <LinhaEstado
            key={loja.id}
            rotulo={lojaLabel(loja)}
            detalhe={loja.ativo ? undefined : "Inativa"}
            valor={marcacoes.get(loja.id) ?? padrao}
            onChange={(estado) => marcar(loja.id, estado)}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancelar
        </Button>
        <Button onClick={handleSalvar} variant="contained" disabled={salvando}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
